import type { TFunction } from "i18next";
import type { NotificationItem } from "./useAdminNotifications";
import { getLocalizedLeagueName } from "../utils/teamNameDisplay";
import { getLocalizedOfficialName, type DisplayMode } from "../utils/nameHelpers";

/** Row of `get_shared_official_link_notices` (migration 20261012094602). */
export interface SharedOfficialLinkNotice {
  membership_id: string;
  official_id: string;
  first_name: string;
  last_name: string;
  first_name_ar: string | null;
  last_name_ar: string | null;
  league_id: string;
  league_name: string;
  league_name_ar: string | null;
  added_at: string;
}

export interface PendingRequestLike {
  id: string;
  created_at: string;
  requires_super_admin?: boolean | null;
  officials?: {
    first_name?: string | null;
    last_name?: string | null;
    first_name_ar?: string | null;
    last_name_ar?: string | null;
  } | null;
}

/** « Officiels » bell items: people of my leagues added to another league; super-admin: shared-official requests. */
export function buildOfficialNoticeItems(input: {
  notices: SharedOfficialLinkNotice[];
  pendingRequests: PendingRequestLike[];
  isSuperAdmin: boolean;
  mode: DisplayMode;
  t: TFunction;
}): NotificationItem[] {
  const { notices, pendingRequests, isSuperAdmin, mode, t } = input;
  const requestItems: NotificationItem[] = isSuperAdmin
    ? pendingRequests
        // Read from the `*` row, never named in the select: before Task 2 the
        // column does not exist (undefined → not listed) and naming it would
        // kill the whole query.
        .filter((r) => r.requires_super_admin === true)
        .map((r) => ({
          id: `official_request-${r.id}`,
          type: "official_request",
          title:
            getLocalizedOfficialName(
              {
                firstName: r.officials?.first_name,
                lastName: r.officials?.last_name,
                firstNameAr: r.officials?.first_name_ar,
                lastNameAr: r.officials?.last_name_ar,
              },
              mode
            ) || t("item_official_fallback"),
          subtitle: t("item_official_request"),
          createdAt: r.created_at,
          route: "/officials-management/requests",
        }))
    : [];
  const linkItems: NotificationItem[] = notices.map((n) => ({
    id: `official_link-${n.membership_id}`,
    type: "official_link",
    title: getLocalizedOfficialName(
      { firstName: n.first_name, lastName: n.last_name, firstNameAr: n.first_name_ar, lastNameAr: n.last_name_ar },
      mode
    ),
    subtitle: t("item_official_link", {
      league: getLocalizedLeagueName({ name: n.league_name, name_ar: n.league_name_ar }, mode),
    }),
    createdAt: n.added_at,
    route: "/officials-management/officials",
    informational: true,
  }));
  return [...requestItems, ...linkItems];
}
